/* ============================================
   Native Possum — np-cart.js
   The ONE place the storefront talks to the cart. scripts/check.js fails
   the build if any other file calls /cart/add, /cart/change, /cart/update
   or fetch('/cart…').

   window.npCart
     add(items | FormData, opts)   items: [{ id, quantity, properties }] or one such object
     change(key, quantity, opts)   by line item KEY, never by index
     remove(key, opts)             = change(key, 0, opts)
     attributes(obj, opts)         set cart attributes ('' removes one); no re-render
     get()                         -> cart object (no re-render)
     refresh(opts)                 re-render registered sections + cart object
     announce(text)                shared polite live region
   opts (all optional)
     control    element or [elements]: aria-busy + disabled while pending
     title      product title for announcements (change/remove)
     waitFor    promise to await before swapping HTML (e.g. a remove fade)
     announce   false = caller announces itself (success and error)
     openBag    add only: false = don't open the bag drawer
     trigger    add only: element focus returns to when the bag closes
     source     label passed on in the events

   Every request runs through ONE serial queue — never two in flight.
   Every request asks for the sections on the page that registered with
   data-np-cart-section="{{ section.id }}" and swaps their HTML from the
   Section Rendering API response, so Liquid is the only cart markup.
     - Several elements may register the same section; each is matched to
       the element with the same id in the response (else by position),
       its attributes are synced and its innerHTML replaced.
     - data-np-cart-permanent (with an id) inside a registered element is
       kept as the SAME node across swaps (for controls other scripts bind).
     - data-np-focus-key on interactive controls keeps focus across swaps;
       if that control is gone, focus goes to the control now in its place,
       else to the first visible match of data-np-focus-fallback
       ("selector|selector" — tried in order) on a registered element.
   Events (document): np:cart:updated { cart, source, items, openBag, trigger }
                      np:cart:error   { message, source }
   Promises reject on every failure (non-OK response, network error, or a
   quantity Shopify capped to stock) with an Error whose message is the
   customer-facing text.
   Depends on: nothing (np-utilities.js not required).
   ============================================ */

(function () {
  'use strict';

  var ROOT = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
  var ROUTES = {
    add: ROOT + 'cart/add.js',
    change: ROOT + 'cart/change.js',
    update: ROOT + 'cart/update.js',
    cart: ROOT + 'cart.js'
  };
  var GENERIC_ERROR = 'Sorry, something went wrong. Please try again.';
  var NETWORK_ERROR = 'We couldn\u2019t reach the store. Check your connection and try again.';

  // ============================================
  // SERIAL QUEUE
  // ============================================
  var queue = Promise.resolve();

  function enqueue(task) {
    var job = queue.then(task);
    queue = job.then(noop, noop);
    return job;
  }

  function noop() {}

  // ============================================
  // LIVE REGION
  // Writes to the first live region that isn't inert: a component can host
  // its own ([data-np-cart-live], e.g. inside the bag drawer, which stays
  // live while npLayer makes the rest of the page inert); otherwise the
  // shared one this module appends to <body>.
  // ============================================
  var bodyRegion = null;
  var announceTimer = null;

  function ensureBodyRegion() {
    if (bodyRegion && document.body && document.body.contains(bodyRegion)) return bodyRegion;
    if (!document.body) return null;
    bodyRegion = document.createElement('div');
    bodyRegion.id = 'npCartLive';
    bodyRegion.setAttribute('role', 'status');
    bodyRegion.setAttribute('aria-live', 'polite');
    bodyRegion.setAttribute('aria-atomic', 'true');
    bodyRegion.setAttribute('data-np-cart-live', '');
    bodyRegion.style.cssText = 'position:absolute;width:1px;height:1px;padding:0;margin:-1px;' +
      'overflow:hidden;clip:rect(0,0,0,0);white-space:nowrap;border:0;';
    document.body.appendChild(bodyRegion);
    return bodyRegion;
  }

  function liveRegion() {
    ensureBodyRegion();
    var regions = document.querySelectorAll('[data-np-cart-live]');
    for (var i = 0; i < regions.length; i++) {
      if (!regions[i].closest('[inert]')) return regions[i];
    }
    return bodyRegion;
  }

  function announce(text) {
    if (!text) return;
    var region = liveRegion();
    if (!region) return;
    clearTimeout(announceTimer);
    document.querySelectorAll('[data-np-cart-live]').forEach(function (r) { r.textContent = ''; });
    announceTimer = setTimeout(function () { region.textContent = text; }, 100);
  }

  // ============================================
  // REQUESTS
  // ============================================
  function readJson(res) {
    return res.text().then(function (text) {
      var data = null;
      try { data = text ? JSON.parse(text) : null; } catch (e) { data = null; }
      if (!res.ok) {
        var msg = data && (typeof data.description === 'string' && data.description
          ? data.description
          : (typeof data.message === 'string' && data.message ? data.message : ''));
        var err = new Error(msg || GENERIC_ERROR);
        err.status = res.status;
        throw err;
      }
      if (!data || typeof data !== 'object') throw new Error(GENERIC_ERROR);
      return data;
    });
  }

  function post(url, payload) {
    var isForm = typeof FormData !== 'undefined' && payload instanceof FormData;
    return fetch(url, {
      method: 'POST',
      credentials: 'same-origin',
      headers: isForm
        ? { 'Accept': 'application/json', 'X-Requested-With': 'XMLHttpRequest' }
        : { 'Content-Type': 'application/json', 'Accept': 'application/json' },
      body: isForm ? payload : JSON.stringify(payload)
    }).then(readJson);
  }

  function getJson(url) {
    return fetch(url, {
      credentials: 'same-origin',
      headers: { 'Accept': 'application/json' }
    }).then(readJson);
  }

  // Section Rendering API (GET) for this page. Never send Accept: application/json
  // here: on collection, product, page and cart URLs Shopify answers that header
  // with the resource's own JSON (or a 404), not the sections. A reply that is
  // missing any asked-for section is a failure, never a silent no-op.
  function getSections(ids) {
    var url = window.location.pathname + '?sections=' + encodeURIComponent(ids.join(','));
    return fetch(url, { credentials: 'same-origin' }).then(readJson).then(function (data) {
      var missing = ids.filter(function (id) { return typeof data[id] !== 'string'; });
      if (missing.length) {
        var err = new Error(GENERIC_ERROR);
        err.missingSections = missing;
        throw err;
      }
      return data;
    });
  }

  function friendlyMessage(err) {
    if (!err) return GENERIC_ERROR;
    if (!err.status && err.name === 'TypeError') return NETWORK_ERROR;
    return err.message || GENERIC_ERROR;
  }

  // ============================================
  // SECTIONS
  // ============================================
  function attrValue(s) {
    return String(s).replace(/["\\]/g, '\\$&');
  }

  function sectionIds() {
    var ids = [];
    document.querySelectorAll('[data-np-cart-section]').forEach(function (el) {
      var id = el.getAttribute('data-np-cart-section');
      if (id && ids.indexOf(id) === -1) ids.push(id);
    });
    return ids;
  }

  function withSections(payload) {
    var ids = sectionIds();
    if (!ids.length) return payload;
    if (typeof FormData !== 'undefined' && payload instanceof FormData) {
      payload.append('sections', ids.join(','));
      payload.append('sections_url', window.location.pathname);
    } else {
      payload.sections = ids.join(',');
      payload.sections_url = window.location.pathname;
    }
    return payload;
  }

  function isVisible(el) {
    return !!el && el.getClientRects().length > 0 && !el.disabled && !el.closest('[inert]');
  }

  function focusables(markers) {
    var out = [];
    markers.forEach(function (m) {
      Array.prototype.push.apply(out, m.querySelectorAll('[data-np-focus-key]'));
    });
    return out.filter(isVisible);
  }

  function sameAttributes(a, b) {
    if (a.attributes.length !== b.attributes.length) return false;
    for (var i = 0; i < b.attributes.length; i++) {
      var at = b.attributes[i];
      if (a.getAttribute(at.name) !== at.value) return false;
    }
    return true;
  }

  function syncAttributes(el, next) {
    Array.prototype.slice.call(el.attributes).forEach(function (at) {
      if (!next.hasAttribute(at.name)) el.removeAttribute(at.name);
    });
    Array.prototype.forEach.call(next.attributes, function (at) {
      if (el.getAttribute(at.name) !== at.value) el.setAttribute(at.name, at.value);
    });
  }

  function swap(el, next) {
    if (el.innerHTML === next.innerHTML && sameAttributes(el, next)) return;
    var keep = {};
    el.querySelectorAll('[data-np-cart-permanent][id]').forEach(function (p) { keep[p.id] = p; });
    syncAttributes(el, next);
    el.innerHTML = next.innerHTML;
    el.querySelectorAll('[data-np-cart-permanent][id]').forEach(function (p) {
      if (keep[p.id]) p.replaceWith(keep[p.id]);
    });
  }

  function restoreFocus(markers, key, index) {
    var list = focusables(markers);
    var target = null;
    if (key) {
      target = list.filter(function (el) { return el.getAttribute('data-np-focus-key') === key; })[0] || null;
    }
    if (!target && index > -1 && list.length) target = list[Math.min(index, list.length - 1)];
    if (!target) {
      for (var i = 0; i < markers.length && !target; i++) {
        var spec = markers[i].getAttribute('data-np-focus-fallback');
        if (!spec) continue;
        var sels = spec.split('|');
        for (var j = 0; j < sels.length && !target; j++) {
          var cand = document.querySelectorAll(sels[j].trim());
          for (var k = 0; k < cand.length; k++) {
            if (cand[k].getClientRects().length && !cand[k].closest('[inert]')) { target = cand[k]; break; }
          }
        }
      }
    }
    if (target && document.activeElement !== target) target.focus();
  }

  function applySections(sections) {
    if (!sections || typeof sections !== 'object') return;
    var parser = new DOMParser();
    Object.keys(sections).forEach(function (id) {
      var html = sections[id];
      if (typeof html !== 'string') return;
      var sel = '[data-np-cart-section="' + attrValue(id) + '"]';
      var markers = Array.prototype.slice.call(document.querySelectorAll(sel));
      if (!markers.length) return;
      var fresh = Array.prototype.slice.call(parser.parseFromString(html, 'text/html').querySelectorAll(sel));
      if (!fresh.length) return;

      // Where is focus now (inside this section's registered elements)?
      var active = document.activeElement;
      var inside = !!active && markers.some(function (m) { return m.contains(active); });
      var key = null;
      var index = -1;
      if (inside) {
        var keyed = active.closest('[data-np-focus-key]');
        key = keyed ? keyed.getAttribute('data-np-focus-key') : null;
        index = keyed ? focusables(markers).indexOf(keyed) : -1;
      }

      markers.forEach(function (el, i) {
        var next = el.id
          ? fresh.filter(function (f) { return f.id === el.id; })[0]
          : fresh[i];
        if (next) swap(el, next);
      });

      // The focused control was replaced (or emptied out) — find its successor.
      if (inside && document.activeElement !== active) restoreFocus(markers, key, index);
    });
  }

  // ============================================
  // BUSY STATE
  // ============================================
  var wasDisabled = new WeakMap();

  function toList(control) {
    if (!control) return [];
    var list = (Array.isArray(control) || (typeof NodeList !== 'undefined' && control instanceof NodeList))
      ? Array.prototype.slice.call(control) : [control];
    return list.filter(function (el) { return el && el.nodeType === 1; });
  }

  function setBusy(controls, on) {
    controls.forEach(function (el) {
      if (on) {
        wasDisabled.set(el, !!el.disabled);
        el.setAttribute('aria-busy', 'true');
        el.disabled = true;
      } else {
        el.removeAttribute('aria-busy');
        if (!wasDisabled.get(el)) el.disabled = false;
        wasDisabled.delete(el);
      }
    });
  }

  // ============================================
  // RUNNER
  // Busy on → (queue) → work → busy off → focus back → swap sections →
  // np:cart:updated → announce. Failure: busy off → np:cart:error →
  // announce → reject.
  // ============================================
  function emit(name, detail) {
    document.dispatchEvent(new CustomEvent(name, { detail: detail }));
  }

  function run(opts, source, work, isAdd) {
    var controls = toList(opts.control);
    var active = document.activeElement;
    var hadFocus = controls.filter(function (c) { return c === active || c.contains(active); })[0] || null;
    setBusy(controls, true);

    function settle() {
      setBusy(controls, false);
      // Disabling a focused control drops focus to <body> — put it back.
      var now = document.activeElement;
      if (hadFocus && (!now || now === document.body) && document.contains(hadFocus)) {
        hadFocus.focus({ preventScroll: true });
      }
    }

    return enqueue(function () {
      return Promise.resolve().then(work).then(function (result) {
        settle();
        applySections(result.sections);
        var detail = {
          cart: result.cart || null,
          source: source,
          items: result.items || null,
          openBag: isAdd ? opts.openBag !== false : false,
          trigger: 'trigger' in opts ? opts.trigger : (controls[0] || null)
        };
        emit('np:cart:updated', detail);
        if (result.warning) {
          emit('np:cart:error', { message: result.warning, source: source });
          if (opts.announce !== false) announce(result.warning);
          var partial = new Error(result.warning);
          partial.cart = detail.cart;
          throw partial;
        }
        if (opts.announce !== false) announce(result.announce);
        return detail.cart;
      }, function (err) {
        settle();
        var message = friendlyMessage(err);
        emit('np:cart:error', { message: message, source: source });
        if (opts.announce !== false) announce(message);
        var out = new Error(message);
        out.status = err && err.status;
        throw out;
      });
    });
  }

  // A line's spoken name: "Koru Crew (Moss, M)" — the same as
  // snippets/np-line-name.liquid; change both together.
  function titleOf(line) {
    if (!line) return 'Item';
    var name = line.product_title || line.title || 'Item';
    if (!line.product_has_only_default_variant && Array.isArray(line.options_with_values)) {
      var values = line.options_with_values.map(function (o) { return o.value; }).filter(Boolean);
      if (values.length) name += ' (' + values.join(', ') + ')';
    }
    return name;
  }

  function findLine(cart, key) {
    var items = (cart && cart.items) || [];
    for (var i = 0; i < items.length; i++) if (items[i].key === key) return items[i];
    return null;
  }

  // ============================================
  // PUBLIC API
  // ============================================
  function add(items, opts) {
    opts = opts || {};
    var isForm = typeof FormData !== 'undefined' && items instanceof FormData;
    var source = opts.source || 'add';
    return run(opts, source, function () {
      var payload;
      if (isForm) {
        payload = items;
      } else {
        var list = Array.isArray(items) ? items : (items && Array.isArray(items.items) ? items.items : [items]);
        payload = { items: list };
      }
      return post(ROUTES.add, withSections(payload)).then(function (added) {
        var sections = added.sections;
        var lines = Array.isArray(added.items) ? added.items : [added];
        var names = lines.map(titleOf).filter(function (t, i, a) { return a.indexOf(t) === i; }).join(', ');
        return getJson(ROUTES.cart).catch(function () { return null; }).then(function (cart) {
          return { cart: cart, sections: sections, items: lines, announce: 'Added ' + names + ' to bag' };
        });
      });
    }, true);
  }

  function change(key, quantity, opts) {
    opts = opts || {};
    key = String(key);
    quantity = Math.max(0, parseInt(quantity, 10) || 0);
    var source = opts.source || (quantity === 0 ? 'remove' : 'change');
    var job = run(opts, source, function () {
      var payload = withSections({ id: key, quantity: quantity });
      return Promise.all([post(ROUTES.change, payload), opts.waitFor]).then(function (r) {
        var cart = r[0];
        var sections = cart.sections;
        delete cart.sections;
        var line = findLine(cart, key);
        var title = opts.title || titleOf(line);
        var result = { cart: cart, sections: sections };
        if (quantity === 0 || !line) {
          result.announce = title + ' removed from bag';
          if (quantity > 0) result.warning = title + ' is no longer available and was removed from your bag';
        } else if (line.quantity < quantity) {
          result.warning = 'Only ' + line.quantity + ' of ' + title + ' available';
        } else {
          result.announce = 'Quantity of ' + title + ' updated to ' + line.quantity;
        }
        return result;
      });
    });
    // A rejected change (e.g. the line was removed in another tab) may mean
    // the page is out of date — re-sync it. Capped quantities already are.
    job.catch(function (err) {
      if (!err || !err.cart) refresh({ source: 'resync' }).catch(noop);
    });
    return job;
  }

  function remove(key, opts) {
    return change(key, 0, opts);
  }

  // Cart attributes (e.g. the EU delivery tick, np-eu-delivery.js). No
  // sections are asked for: nothing on screen changes, so focus stays put.
  function attributes(attrs, opts) {
    opts = opts || {};
    if (!('announce' in opts)) opts.announce = false;
    return run(opts, opts.source || 'attributes', function () {
      return post(ROUTES.update, { attributes: attrs || {} }).then(function (cart) {
        return { cart: cart, sections: null };
      });
    });
  }

  function get() {
    return enqueue(function () { return getJson(ROUTES.cart); });
  }

  function refresh(opts) {
    opts = opts || {};
    if (!('announce' in opts)) opts.announce = false;
    return run(opts, opts.source || 'refresh', function () {
      var ids = sectionIds();
      var sectionsReq = ids.length ? getSections(ids) : Promise.resolve(null);
      return Promise.all([getJson(ROUTES.cart), sectionsReq]).then(function (r) {
        return { cart: r[0], sections: r[1] };
      });
    });
  }

  window.npCart = {
    add: add,
    change: change,
    remove: remove,
    attributes: attributes,
    get: get,
    refresh: refresh,
    announce: announce,
    sectionIds: sectionIds
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', ensureBodyRegion);
  } else {
    ensureBodyRegion();
  }

  // Back/forward cache restores a stale page — re-sync the cart UI.
  window.addEventListener('pageshow', function (e) {
    if (e.persisted) refresh({ source: 'pageshow' }).catch(noop);
  });
})();
